<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { useBookingsStore } from '@/stores/bookings';
import { useMessagesStore } from '@/stores/messages';

/**
 * The phone's primary navigation: the places you go daily live here, not in the
 * hamburger. Messages, Bookings and Profile are guarded routes — for a signed-out
 * visitor the router turns the tap into the login page, which resumes the journey
 * after. Provider and admin destinations live inside Profile, not the bar.
 *
 * Hidden entirely at desktop widths, where the topbar carries the same links.
 */
const messages = useMessagesStore();
const bookings = useBookingsStore();
</script>

<template>
  <nav class="tabbar" aria-label="Primary">
    <RouterLink class="tab" to="/" exact-active-class="tab--on" active-class="tab--maybe">
      <span class="tab__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 10.6 12 3.5l9 7.1" />
          <path d="M5.6 9.4V20.5h12.8V9.4" />
        </svg>
      </span>
      <span>Home</span>
    </RouterLink>

    <RouterLink class="tab" to="/messages" active-class="tab--on">
      <span class="tab__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
          <path d="m4 6.5 8 6 8-6" />
        </svg>
        <span v-if="messages.unread > 0" class="tab__badge" :aria-label="`${messages.unread} unread messages`">
          {{ messages.unread > 9 ? '9+' : messages.unread }}
        </span>
      </span>
      <span>Messages</span>
    </RouterLink>

    <RouterLink class="tab" to="/browse" active-class="tab--on">
      <span class="tab__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true">
          <circle cx="10.8" cy="10.8" r="6.3" />
          <path d="m15.6 15.6 4.9 4.9" />
        </svg>
      </span>
      <span>Search</span>
    </RouterLink>

    <RouterLink class="tab" to="/bookings" active-class="tab--on">
      <span class="tab__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="4.5" y="4" width="15" height="16.5" rx="2.5" />
          <path d="M9 3.2h6a1 1 0 0 1 1 1V6a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V4.2a1 1 0 0 1 1-1Z" />
          <path d="M8.5 11h7M8.5 15h4" />
        </svg>
        <span v-if="bookings.pending > 0" class="tab__badge" :aria-label="`${bookings.pending} pending bookings`">
          {{ bookings.pending > 9 ? '9+' : bookings.pending }}
        </span>
      </span>
      <span>Bookings</span>
    </RouterLink>

    <RouterLink class="tab" to="/profile" active-class="tab--on">
      <span class="tab__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="8.2" r="3.7" />
          <path d="M5 20c0-3.6 3.1-5.8 7-5.8s7 2.2 7 5.8" />
        </svg>
      </span>
      <span>Profile</span>
    </RouterLink>
  </nav>
</template>

<style scoped>
.tabbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  /* Under the topbar's flyouts (z 10): the sheet's scrim must dim this bar too. */
  z-index: 9;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  height: calc(var(--tabbar-h) + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  border-top: 1px solid var(--border);
  background: var(--bg);
}
@supports (backdrop-filter: blur(12px)) {
  .tabbar {
    background: color-mix(in srgb, var(--bg) 82%, transparent);
    backdrop-filter: blur(12px) saturate(1.4);
  }
}

.tab {
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 0.2rem;
  min-width: 0;
  color: var(--muted);
  text-decoration: none;
  font-size: 0.66rem;
  font-weight: 550;
  letter-spacing: 0.01em;
  transition: color 0.15s ease;
}
.tab svg { width: 22px; height: 22px; }
.tab > span:last-child { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* The pill that sits behind the icon and fills with the accent tint when the tab
   is active. Also hosts the unread badge, which floats over its top-right corner. */
.tab .tab__icon {
  position: relative;
  display: grid;
  place-items: center;
  width: 3.4rem;
  height: 1.9rem;
  border-radius: var(--radius-pill);
  overflow: visible;
  background: transparent;
  transition: background-color 0.18s ease;
}
.tab__badge {
  position: absolute;
  top: -5px;
  left: 60%;
  min-width: 15px;
  height: 15px;
  padding: 0 3px;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  border-radius: var(--radius-pill);
  background: var(--danger);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1;
}

/* `to="/"` prefix-matches everything; only the exact match may light the Home tab.
   On the exact match both classes are present, so `--on` must be declared last to win. */
.tab--maybe { color: var(--muted); }
.tab--maybe .tab__icon { background: transparent; }
.tab--on { color: var(--accent); }
.tab--on .tab__icon { background: var(--accent-weak); }

@media (min-width: 720px) {
  .tabbar { display: none; }
}
</style>
